<template>
	<ValidationForm #default="{ handleSubmit }">
		<SlidingSheet
			:title="modalTitle"
			:is-open="isOpen"
			width="800px"
			@close="$emit('close-modal')"
		>
			<!-- <h3 class="question-title">
				Which Cassie Configuration Item would you like to manage?
			</h3>
			<p
				class="question-description mr-5"
			>
				description?
			</p> -->
			<v-row dense>
				<v-col cols="6">
					<Dropdown
						v-model="cassieModule"
						:disabled="!userFullPermissions || shouldLockModuleAndGroup"
						label="Cassie Configuration Item"
						test-id="sso-configuration-cassie-module-dropdown"
						tooltip-text="Select the Cassie Configuration Item to map SSO provider claims to. Choose 'User Roles' to link the provider's role information to user access levels within Cassie, or 'Brands' to link your users brand affiliations to those configured in Cassie."
						:items="cassieModuleItems"
					/>
				</v-col>
				<v-col cols="6">
					<!-- SCIM Groups Dropdown - shown when SCIM is enabled -->
					<Dropdown
						v-if="showScimGroupDropdown"
						v-model="selectedScimGroupGuid"
						:disabled="!userFullPermissions || (shouldLockModuleAndGroup && (cassieModule === USER_ROLES || cassieModule === BRANDS))"
						test-id="sso-configuration-scim-group"
						tooltip-text="Select the SCIM Group to map to this Cassie configuration."
						label="SCIM Group"
						:items="scimGroupItems"
					/>
					<!-- Regular text field - shown when SCIM is disabled -->
					<TextField
						v-else
						v-model="groupName"
						:disabled="cassieModule === PERSONAL_INFO || !userFullPermissions || (shouldLockModuleAndGroup && (cassieModule === USER_ROLES || cassieModule === BRANDS))"
						test-id="sso-configuration-group-name"
						tooltip-text="Specify the name of the claim sent by your Identity Provider that contains the user's roles or group memberships (e.g., groups). For example, if the JWT includes a claim named 'roles' or 'groups' that lists the user's permissions, enter 'roles' or 'groups' here. This specifies which part of the SSO token your application should inspect for mapping."
						:label="selectedProvider.claimMappingDisplayName"
					/>
				</v-col>
			</v-row>
			<v-row class="my-1">
				<v-col cols="12">
					<v-divider />
				</v-col>
			</v-row>
			<v-row dense>
				<v-col cols="6">
					<Dropdown
						v-model="cassieComponent"
						:disabled="!userFullPermissions"
						:label="cassieComponentDropdownLabel"
						test-id="sso-configuration-cassie-component-dropdown"
						tooltip-text="Select the Cassie User Role or Brand that you are looking to map against your SSO Claim. The available options depend on whether 'User Roles', 'Brands' or 'Personal Information' is selected above and what is configured within Cassie."
						:items="getComponentItems"
					/>
				</v-col>
				<!-- Entra ID Role Name field - hidden when SCIM is enabled (for non-personal modules) -->
				<v-col
					v-if="!scimEnabled || cassieModule === PERSONAL_INFO"
					cols="6"
				>
					<TextField
						v-model="claimName"
						:disabled="!userFullPermissions"
						:rules="{ max: 255 }"
						test-id="sso-configuration-claim-name"
						tooltip-text="Specify the SSO claim attribute name whose value you want to map to the selected Cassie User Role or Brand."
						:label="selectedProvider.claimAttributeDisplayName"
					/>
				</v-col>
			</v-row>
			<v-row>
				<v-spacer />
				<v-col
					cols="4"
					class="text-right"
				>
					<PrimaryActionButton
						v-if="userFullPermissions"
						:disabled="!canAdd"
						@click="handleSubmit(addClaim)"
					>
						Add
					</PrimaryActionButton>
				</v-col>
			</v-row>
			<v-row>
				<v-col cols="12">
					<DataTable
						:headers="tableHeaders"
						:items="ssoMapping"
						:disable-sort="true"
					>
						<template #[`item.cassieModuleName`]="{ item }">
							{{ getComponentTypeName(item.componentTypeId) }}
						</template>
						<template #[`item.scimGroupNameDisplay`]="{ item }">
							<span :class="{ 'warning-text': scimEnabled && !isScimMappingValid(item) }">
								{{ getScimGroupNameDisplay(item) }}
							</span>
							<v-icon
								v-if="scimEnabled && !isScimMappingValid(item)"
								small
								color="warning"
								class="ml-1"
								title="This mapping was created before SCIM was enabled and may need to be updated"
								aria-label="Legacy mapping may need to be updated"
								role="img"
							>
								mdi-alert
							</v-icon>
						</template>
						<template #[`item.typeDisplay`]="{ item }">
							{{ getTypeColumnDisplay(item) }}
						</template>
						<template #[`item.order`]="{ index }">
							{{ index + 1 }}
						</template>
						<template #[`item.action`]="{ item, index }">
							<div style="display: flex; align-items: right; justify-content: center; gap: 4px; white-space: nowrap;">
								<v-btn
									v-if="index !== 0 && userFullPermissions"
									icon
									small
									@click="moveUp(index, item)"
								>
									<v-icon small>
										mdi-arrow-up
									</v-icon>
								</v-btn>
								<v-btn
									v-if="index !== ssoMapping.length - 1 && userFullPermissions"
									icon
									small
									@click="moveDown(index, item)"
								>
									<v-icon small>
										mdi-arrow-down
									</v-icon>
								</v-btn>
								<v-menu
									v-if="userFullPermissions"
									offset-y
									left
								>
									<template #activator="{ on, attrs }">
										<v-btn
											icon
											small
											v-bind="attrs"
											v-on="on"
											@click.stop
										>
											<v-icon small>
												mdi-dots-horizontal
											</v-icon>
										</v-btn>
									</template>
									<v-list dense>
										<v-list-item @click="removeClaim(item)">
											<v-list-item-content>
												<v-list-item-title>
													Delete
												</v-list-item-title>
											</v-list-item-content>
										</v-list-item>
									</v-list>
								</v-menu>
							</div>
						</template>
					</DataTable>
				</v-col>
			</v-row>
			<template #footer>
				<div class="d-flex justify-space-between">
					<SecondaryActionButton @click="$emit('close-modal')">
						Cancel
					</SecondaryActionButton>
					<PrimaryActionButton
						v-if="userFullPermissions"
						class="ml-2"
						:disabled="!canConfirm"
						@click="handleSubmit(updateClaims)"
					>
						Confirm
					</PrimaryActionButton>
				</div>
			</template>
		</SlidingSheet>
	</ValidationForm>
</template>

<script>
import { SlidingSheet } from '../../../../components/component-library-wrapper.js'
import ValidationForm from '../../../../../../shared/components/validation-form.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import TextField from '../../../../../../shared/components/text-field.vue'
import DataTable from '../../../../../../shared/components/data-table.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import { brandOptions } from '../../../../../../shared/state/brands.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
export default {
	components: {
		ValidationForm,
		SlidingSheet,
		Dropdown,
		TextField,
		DataTable,
		PrimaryActionButton,
		SecondaryActionButton
	},
	props: {
		isOpen: {
			type: Boolean,
			default: false
		},
		ssoConfiguration: Object,
		userFullPermissions: Boolean,
		lookupData: Object,
		ssoMappingToManage: Array,
		productRoles: Array,
		scimEnabled: { type: Boolean, default: false },
		scimGroups: { type: Array, default: () => [] }
	},
	setup () {
		return {
			brandOptions,
			showSnackbar
		}
	},
	data () {
		const USER_ROLES = 1
		const BRANDS = 2
		const PERSONAL_INFO = 3
		return {
			ssoMapping: [],
			cassieModule: null,
			groupName: null,
			selectedScimGroupGuid: null,
			cassieComponent: null,
			claimName: null,
			USER_ROLES,
			BRANDS,
			PERSONAL_INFO
		}
	},
	computed: {
		modalTitle () {
			return `${this.isEdit ? 'Edit' : 'Add'} SSO Mapping`
		},
		selectedProviderName () {
			const provider = this.lookupData?.ssoProviders?.find(p => p.id === this.ssoConfiguration?.ssoProviderId)
			return provider?.ssoProviderName || ''
		},
		isEdit () {
			return !!this.ssoMappingToManage?.length
		},
		containsClaim () {
			return this.ssoMapping.some(mapping => mapping.componentTypeId === this.cassieModule)
		},
		selectedProvider () {
			return this.lookupData.ssoProviders.find(provider => provider.id === this.ssoConfiguration.ssoProviderId)
		},
		cassieModuleItems () {
			const alreadyMappedIds = this.ssoConfiguration.mappedClaims
				.map(claim => claim.claims?.[0]?.componentTypeId)
				.filter(id => id !== undefined)
			const baseItems = this.lookupData.ssoComponentTypes.map(m => ({
				text: m.componentTypeName,
				value: m.id
			}))

			if (!baseItems.find(i => i.value === this.PERSONAL_INFO)) {
				baseItems.push({ text: 'Personal Information', value: this.PERSONAL_INFO })
			}

			const items = baseItems.filter(item => !alreadyMappedIds.includes(item.value))
			if (this.isEdit && this.cassieModule != null && !items.find(i => i.value === this.cassieModule)) {
				const current = baseItems.find(i => i.value === this.cassieModule)
				if (current) items.push(current)
			}
			return items
		},
		shouldLockModuleAndGroup () {
			return this.ssoMapping.length > 0
		},
		tableHeaders () {
			return [
				{
					text: 'Cassie Configuration Item',
					value: 'cassieModuleName',
					width: '15%'
				},
				{
					// When SCIM enabled: "SCIM Group Name", otherwise provider claim attribute
					text: this.scimEnabled ? 'SCIM Group Name' : this.selectedProvider.claimAttributeDisplayName,
					value: 'scimGroupNameDisplay',
					width: '20%'
				},
				{
					// When SCIM enabled: "Type", otherwise provider group name
					text: this.scimEnabled ? 'Type' : this.selectedProvider.claimMappingDisplayName,
					value: 'typeDisplay',
					width: '15%'
				},
				{
					text: 'Cassie Component',
					value: 'cassieComponentName',
					width: '20%'
				},
				{
					text: 'Order',
					value: 'order',
					width: '10%'
				},
				{
					text: 'Action',
					value: 'action',
					width: '80px',
					align: 'right',
					sortable: false
				}
			]
		},
		getComponentItems () {
			switch (this.cassieModule) {
				case this.USER_ROLES:
					return this.productRoles.map(r => ({
						text: r.name,
						value: r.roleId
					}))
				case this.BRANDS:
					return brandOptions.value
				case this.PERSONAL_INFO: {
					const base = [
						{ text: 'Forename', value: 1 },
						{ text: 'Surname', value: 2 }
					]
					if (this.ssoConfiguration.productId === 2) {
						base.push({ text: 'Email Address', value: 5 })
					}
					if (this.ssoConfiguration.productId === 1) {
						base.push(
							{ text: 'Telephone Number', value: 3 },
							{ text: 'Mobile Number', value: 4 }
						)
					}
					return base
				}
				default:
					return []
			}
		},
		cassieComponentDropdownLabel () {
			switch (this.cassieModule) {
				case this.USER_ROLES:
					return 'Cassie User Role'
				case this.BRANDS:
					return 'Cassie Brand'
				case this.PERSONAL_INFO:
					return 'Cassie Personal Information Field'
				default:
					return 'Cassie Component'
			}
		},
		canAdd () {
			if (!this.userFullPermissions) return false
			const hasModule = !!this.cassieModule
			const needsGroup = this.cassieModule !== this.PERSONAL_INFO
			// When SCIM is enabled, check for selected SCIM group; otherwise check groupName
			const hasGroup = needsGroup
				? (this.scimEnabled ? !!this.selectedScimGroupGuid : !!this.groupName)
				: true
			const hasComponent = this.cassieComponent !== null && this.cassieComponent !== undefined
			// When SCIM is enabled for non-personal modules, claimName is not required
			const needsClaimName = !this.scimEnabled || this.cassieModule === this.PERSONAL_INFO
			const hasClaimName = needsClaimName ? !!this.claimName : true
			if (!hasModule || !hasGroup || !hasComponent || !hasClaimName) return false
			// Check if this would be a duplicate mapping
			return !this.wouldBeDuplicate
		},
		wouldBeDuplicate () {
			// Check if the current selection would create a duplicate mapping
			if (!this.cassieModule) return false
			if (this.cassieComponent === null || this.cassieComponent === undefined) return false
			return this.ssoMapping.some(m => {
				if (m.componentTypeId !== this.cassieModule) return false
				if (m.cassieComponentId !== this.cassieComponent) return false
				// For SCIM mode, check by scimGroupGuid; otherwise check externalComponentName
				if (this.scimEnabled && this.cassieModule !== this.PERSONAL_INFO) {
					return m.scimGroupGuid === this.selectedScimGroupGuid
				}
				return m.externalComponentName === this.claimName
			})
		},
		canConfirm () {
			return this.userFullPermissions && this.ssoMapping.length > 0
		},
		scimGroupItems () {
			return (this.scimGroups || []).map(g => ({
				text: g.displayName,
				value: g.groupGuid
			}))
		},
		showScimGroupDropdown () {
			return this.scimEnabled && this.cassieModule !== this.PERSONAL_INFO && this.scimGroupItems.length > 0
		},
		selectedScimGroupName () {
			if (!this.selectedScimGroupGuid) return ''
			const group = (this.scimGroups || []).find(g => g.groupGuid === this.selectedScimGroupGuid)
			return group ? group.displayName : ''
		}
	},
	watch: {
		cassieModule (val) {
			if (val === this.PERSONAL_INFO) {
				this.groupName = ''
			}
		}
	},
	created () {
		if (this.isEdit) {
			this.ssoMapping = [
				...this.ssoMappingToManage
			]
			this.cassieModule = this.ssoMapping[0].componentTypeId
			// Set groupName or selectedScimGroupGuid based on existing data
			if (this.scimEnabled && this.ssoMapping[0].scimGroupGuid) {
				this.selectedScimGroupGuid = this.ssoMapping[0].scimGroupGuid
			} else {
				this.groupName = this.ssoMapping[0].componentTypeExternalName
			}
		}
	},
	methods: {
		getComponentTypeName (componentTypeId) {
			return this.lookupData.ssoComponentTypes.find(module => module.id === componentTypeId).componentTypeName
		},
		getGroupDisplayName (item) {
			if (item.componentTypeExternalName === 'personal_information') return ''
			// If SCIM is enabled and we have a SCIM group name stored, show that
			if (this.scimEnabled && item.scimGroupName) {
				return item.scimGroupName
			}
			// If SCIM is enabled and componentTypeExternalName is 'scim_group', try to look up the name
			if (this.scimEnabled && item.componentTypeExternalName === 'scim_group' && item.scimGroupGuid) {
				const group = (this.scimGroups || []).find(g => g.groupGuid === item.scimGroupGuid)
				return group ? group.displayName : item.componentTypeExternalName
			}
			return item.componentTypeExternalName
		},
		getScimGroupNameDisplay (item) {
			// For non-SCIM mode or personal module, show the externalComponentName (Entra ID Role Name)
			if (!this.scimEnabled || this.cassieModule === this.PERSONAL_INFO) {
				return item.externalComponentName
			}
			// For SCIM mode: show the SCIM group name
			if (item.scimGroupName) {
				return item.scimGroupName
			}
			if (item.scimGroupGuid) {
				const group = (this.scimGroups || []).find(g => g.groupGuid === item.scimGroupGuid)
				if (group) return group.displayName
			}
			// For legacy mappings (before SCIM was enabled), show the externalComponentName
			return item.externalComponentName || '—'
		},
		getTypeColumnDisplay (item) {
			// For non-SCIM mode, show the provider group (componentTypeExternalName)
			if (!this.scimEnabled) {
				if (item.componentTypeExternalName === 'personal_information') return '—'
				return item.componentTypeExternalName
			}
			// For SCIM mode:
			if (item.componentTypeExternalName === 'scim_group') {
				return 'scim_group'
			}
			// For legacy mappings, show the old componentTypeExternalName
			if (item.componentTypeExternalName === 'personal_information') return '—'
			return item.componentTypeExternalName
		},
		isScimMappingValid (item) {
			if (!this.scimEnabled) return true
			if (item.componentTypeExternalName === 'scim_group') return true
			// For legacy mappings, check if the externalComponentName matches any SCIM group
			const matchingGroup = (this.scimGroups || []).find(g =>
				g.displayName === item.externalComponentName
			)
			return !!matchingGroup
		},
		moveDown (index) {
			this.move(this.ssoMapping, index, index + 1)
		},
		moveUp (index) {
			this.move(this.ssoMapping, index, index - 1)
		},
		applySort (array) {
			this.ssoMapping = array
		},
		move (array, from, to) {
			const copy = array.slice()
			const [moved] = copy.splice(from, 1)
			copy.splice(to, 0, moved)
			return this.applySort(copy)
		},
		removeClaim (claim) {
			this.ssoMapping = this.ssoMapping.filter(mapping => mapping !== claim).map((mapping, index) => ({
				...mapping,
				displayOrder: index + 1
			}))
		},
		addClaim () {
			if (!this.canAdd) return

			// Determine componentTypeExternalName and scimGroupGuid based on SCIM mode
			let componentTypeExternalName
			let scimGroupGuid = null
			let scimGroupName = null
			let externalComponentName = this.claimName

			if (this.cassieModule === this.PERSONAL_INFO) {
				componentTypeExternalName = 'personal_information'
			} else if (this.scimEnabled) {
				componentTypeExternalName = 'scim_group'
				scimGroupGuid = this.selectedScimGroupGuid
				scimGroupName = this.selectedScimGroupName
				// When SCIM is enabled, store the SCIM group name in externalComponentName for the API
				externalComponentName = scimGroupName
			} else {
				componentTypeExternalName = this.groupName
			}

			const claim = {
				cassieComponentId: this.cassieComponent,
				cassieComponentName: this.getComponentItems.find(item => item.value === this.cassieComponent).text,
				componentTypeExternalName,
				componentTypeId: this.cassieModule,
				displayOrder: this.ssoMapping.length + 1,
				externalComponentName,
				ssoClaimId: 0,
				scimGroupGuid,
				scimGroupName,
				scimExternalId: scimGroupGuid // Send SCIM group GUID as scimExternalId for the API
			}
			// Check for duplicate: same Cassie component + same SCIM group (or same external name in non-SCIM mode)
			const isDuplicate = this.ssoMapping.some(m => {
				if (m.componentTypeId !== claim.componentTypeId) return false
				if (m.cassieComponentId !== claim.cassieComponentId) return false
				// For SCIM mode, check by scimGroupGuid; otherwise check externalComponentName
				if (this.scimEnabled && this.cassieModule !== this.PERSONAL_INFO) {
					return m.scimGroupGuid === claim.scimGroupGuid
				}
				return m.externalComponentName === claim.externalComponentName
			})
			if (isDuplicate) {
				// Silently prevent duplicate - user feedback could be added here if needed
				return
			}
			this.ssoMapping.push(claim)
			if (this.cassieModule === this.PERSONAL_INFO) {
				this.groupName = null
			}
			this.cassieComponent = null
			this.claimName = null
		},
		updateClaims () {
			if (!this.ssoMapping.length) {
				showSnackbar({ text: 'Please add at least one claim', color: 'red' })
				return
			}
			const ssoMapping = this.ssoMapping.map((mapping, index) => ({
				...mapping,
				displayOrder: index + 1
			}))
			this.$emit('update-sso-mapping', ssoMapping)
		}
	}
}
</script>

<style scoped>
.question-title {
	margin: 0;
	font-weight: 500;
	font-size: 14px;
	line-height: 1.4;
}

.question-description {
	margin: 0;
	font-size: 12px;
	line-height: 1.5;
}

.warning-text {
	color: var(--v-warning-base, #fb8c00);
}
</style>
